/**
 * @fileoverview Plugin Pinia Persisted State con crittografia SecureLS
 *
 * Plugin per la persistenza automatica degli store Pinia utilizzando
 * SecureLS per la crittografia dei dati salvati nel localStorage del browser.
 * Mantiene la stessa configurazione e compatibilità del plugin Vuex esistente.
 *
 * @author Migrazione Vuex → Pinia
 * @since 2025-09-24
 */

import { createPersistedState } from 'pinia-plugin-persistedstate';
import SecureLS from 'secure-ls';

/**
 * Interfaccia per l'oggetto di configurazione SecureLS
 */
interface SecureLSConfig {
	/** Se abilitare la compressione dei dati (default: false per compatibilità) */
	isCompression: boolean;
}

/**
 * Configurazione SecureLS per la crittografia
 * (Identica al plugin Vuex per garantire compatibilità)
 *
 * @constant
 * @type {SecureLSConfig}
 */
const SECURE_LS_CONFIG: SecureLSConfig = {
	/** Disabilita compressione per compatibilità e debugging */
	isCompression: false,
};

// Istanza globale di SecureLS per la crittografia
const secureStorage = new SecureLS(SECURE_LS_CONFIG);

/**
 * Adattatore storage personalizzato per Pinia
 * Wrappa SecureLS per renderlo compatibile con pinia-plugin-persistedstate
 *
 * **Caratteristiche:**
 * - 🔒 **Crittografia automatica** di tutti i dati persistiti
 * - 🔄 **Compatibilità Vuex** - usa stesse chiavi e crittografia
 * - 🛡️ **Error handling** robusto per evitare crash
 * - 📱 **Client-side only** per compatibilità SSR
 */
const secureStorageAdapter = {
	/**
	 * Recupera e decrittografa un valore dal localStorage
	 *
	 * @param {string} key - Chiave del valore da recuperare
	 * @returns {string | null} Valore decrittografato serializzato come JSON
	 */
	getItem: (key: string): string | null => {
		try {
			const value = secureStorage.get(key);
			if (value === null || value === undefined) {
				return null;
			}
			// pinia-plugin-persistedstate si aspetta una stringa JSON
			return JSON.stringify(value);
		} catch (error) {
			console.error('❌ ERROR pinia-persisted.client.dati:', error);
			console.warn(`[Pinia Persisted] Error retrieving ${key}:`, error);
			return null;
		}
	},

	/**
	 * Crittografa e salva un valore nel localStorage
	 *
	 * @param {string} key - Chiave sotto cui salvare
	 * @param {string} value - Valore JSON serializzato da salvare
	 */
	setItem: (key: string, value: string): void => {
		try {
			// Deserializza il JSON prima di crittografare
			const parsedValue = JSON.parse(value);
			secureStorage.set(key, parsedValue);
		} catch (error) {
			console.error(`[Pinia Persisted] Error saving ${key}:`, error);
		}
	},

	/**
	 * Rimuove un valore dal localStorage
	 *
	 * @param {string} key - Chiave del valore da rimuovere
	 */
	removeItem: (key: string): void => {
		try {
			secureStorage.remove(key);
		} catch (error) {
			console.error('❌ ERROR pinia-persisted.client.warn:', error);
			console.warn(`[Pinia Persisted] Error removing ${key}:`, error);
		}
	},
};

/**
 * Plugin di persistenza Pinia configurato
 *
 * Utilizza il nostro adattatore SecureLS personalizzato e configurazioni
 * ottimizzate per l'ambiente di sviluppo.
 *
 * **Configurazione:**
 * - Storage crittografato con SecureLS
 * - Debug abilitato in development
 * - Compatibilità con setup Vuex esistente
 *
 * @example
 * ```typescript
 * // Usage negli store Pinia:
 * export const useAuthStore = defineStore('auth', {
 *   state: () => ({ ... }),
 *   persist: {
 *     key: 'auth',  // Stessa chiave del setup Vuex
 *     paths: ['accessToken', 'userInfo']
 *   }
 * })
 * ```
 */
export const piniaPersistedState = createPersistedState({
	/** Storage personalizzato con crittografia SecureLS */
	storage: secureStorageAdapter,

	/** Abilita debug in development per troubleshooting */
	debug: process.env.NODE_ENV === 'development',

	/**
	 * Serializer personalizzato (opzionale)
	 * Il plugin usa JSON.stringify/parse di default, che è quello che vogliamo
	 */
});

/**
 * Export di default per l'utilizzo come plugin
 */
export default piniaPersistedState;
